import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

// Cache-Buster
const addCacheBuster = (path) => {
  if (!path) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}t=${Date.now()}`;
};

const ImagesPage = () => {
  const { t } = useLanguage();
  const [filter, setFilter] = useState('all');

  const categories = [
    { id: 'all', name: t('images.allImages') },
    { id: 'branding', name: t('images.branding') },
    { id: 'products', name: t('images.products') },
    { id: 'farm', name: t('images.farm') },
    { id: 'production', name: t('images.production') }
  ];

  const imageConfig = [
    {
      id: 1,
      category: 'branding',
      src: '/images/logo.webp',
      alt: 'Svansele Grönt Logo'
    },
    {
      id: 2,
      category: 'branding',
      src: '/images/hallo.webp',
      alt: 'Hallo'
    },
    {
      id: 3,
      category: 'products',
      src: '/images/mohrueben.webp',
      alt: 'Morötter'
    },
    {
      id: 4,
      category: 'products',
      src: '/images/kaal.webp',
      alt: 'Kål'
    },
    {
      id: 5,
      category: 'products',
      src: '/images/spetskal.webp',
      alt: 'Spetskål'
    },
    {
      id: 6,
      category: 'farm',
      src: '/images/skoerd_7.webp',
      alt: 'Olof på skörden'
    },
    {
      id: 7,
      category: 'farm',
      src: '/images/skoerd_11.webp',
      alt: 'Jobb'
    },
    {
      id: 8,
      category: 'farm',
      src: '/images/ernte_1.webp',
      alt: 'Skörden'
    },
    {
      id: 9,
      category: 'farm',
      src: '/images/adak_1.webp',
      alt: 'Adak'
    },
    {
      id: 10,
      category: 'farm',
      src: '/images/gaardsdag_1.webp',
      alt: 'Farmday'
    },
   {
      id: 11,
      category: 'farm',
      src: '/images/skelleftea_1.webp',
      alt: 'Skellefteå'
    },
    {
      id: 12,
      category: 'production',
      src: '/images/vaexthuset.webp',
      alt: 'Växthuset'
    },
   {
      id: 13,
      category: 'farm',
      src: '/images/skoerd_2.webp',
      alt: 'Sörden'
    },
   {
      id: 14,
      category: 'farm',
      src: '/images/saat.JPG',
      alt: 'Säde'
    },
   {
      id: 15,
      category: 'farm',
      src: '/images/morot_ung.webp',
      alt: 'Ung morot'
    }
  ];

  // Bei jedem Render frische URLs
  const images = imageConfig.map((image) => ({
    ...image,
    src: addCacheBuster(image.src)
  }));

  const filteredImages = filter === 'all' 
    ? images 
    : images.filter(img => img.category === filter);

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            {t('images.title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('images.subtitle')}
          </p>
        </motion.div>

        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setFilter(cat.id)}
              className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 ${
                filter === cat.id
                  ? 'bg-primary-green text-white shadow-lg transform scale-105'
                  : 'bg-white text-gray-600 hover:bg-gray-100 shadow-sm'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Gallery */}
        <motion.div 
          layout
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6"
        >
          <AnimatePresence>
            {filteredImages.map((image) => (
              <motion.div
                layout
                key={image.id}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.4 }}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 group cursor-pointer"
              >
                <div className="overflow-hidden aspect-w-4 aspect-h-3">
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-64 object-cover transform group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Social Media Link */}
        <div className="text-center mt-16">
          <p className="text-gray-600 mb-4">
            {t('images.followText')}
          </p>
          <a
            href="https://www.facebook.com/svansele.gront"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-primary-green font-bold hover:underline"
          >
            {t('images.followUs')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default ImagesPage;